import {
  Card,
  CardHeader,
  CardBody,
  Typography,
} from "@material-tailwind/react";
import { useNavigate } from "react-router-dom";
import {
  FilmIcon,
  MusicalNoteIcon,
  BookOpenIcon,
  EyeIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { deepCopy, returnSubCategoryName, toFixed } from "../helper/helper";
import { useReduxStore } from "../hooks/useReduxStore";
import { updateItem } from "../store/Reducers/itemReducer";
import { getItemAccessType } from "../helper/accessTypeHelper";

export default function HorizontalListCard({
  image,
  title,
  id,
  description,
  rating,
  type = "movie",
  views = 1,
  item = {},
  cast,
  sub_category = [],
  category,
  author,
  price,
  exclusive_content,
  ppv_content,
  dark = false,
  layout = "grid", // "grid" | "list"
}) {
  const { dispatch } = useReduxStore();
  const navigate = useNavigate();

  const currentItem = {
    ...item,
    price: price !== undefined ? price : item?.price,
    exclusive_content:
      exclusive_content !== undefined
        ? exclusive_content
        : item?.exclusive_content,
    ppv_content:
      ppv_content !== undefined ? ppv_content : item?.ppv_content,
    type: type || item?.type,
  };

  const access = getItemAccessType(currentItem);

  const onClickCard = () => {
    dispatch(updateItem({ itemDetail: deepCopy(currentItem) }));
    navigate(
      {
        pathname: "/Detail",
        search: `?id=${currentItem?.id || id}`,
      },
      { state: { ...currentItem } }
    );
  };

  const dummuImageUrl =
    "https://images.unsplash.com/photo-1499696010180-025ef6e1a8f9?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80";

  // STREAMING LIST VIEW (Mobile audio/track row format)
  if (layout === "list") {
    return (
      <div
        onClick={onClickCard}
        className={`group w-full flex items-center justify-between p-2.5 sm:p-3 rounded-2xl border transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md active:scale-[0.99] gap-3 ${
          dark
            ? "bg-zinc-900/60 border-zinc-800/80 hover:bg-zinc-850 hover:border-zinc-700 text-white shadow-black/20"
            : "bg-white border-gray-150 hover:bg-orange-50/20 hover:border-orange-200 text-black shadow-gray-200/40"
        }`}
      >
        {/* Left: Square Thumbnail */}
        <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden flex-shrink-0 bg-zinc-950 border border-white/5 shadow-md">
          <img
            src={image || item?.image || dummuImageUrl}
            alt={title || item?.title || "Thumbnail"}
            onError={(e) => {
              if (e.currentTarget.src !== dummuImageUrl) {
                e.currentTarget.src = dummuImageUrl;
              }
            }}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute bottom-1 right-1 p-0.5 rounded-md bg-black/75 backdrop-blur-sm text-secondary">
            {(type === "Video" || currentItem?.type === "Video") && (
              <FilmIcon className="h-3 w-3" />
            )}
            {(type === "Audio" || currentItem?.type === "Audio") && (
              <MusicalNoteIcon className="h-3 w-3" />
            )}
            {(type === "Book" || currentItem?.type === "Book") && (
              <BookOpenIcon className="h-3 w-3" />
            )}
          </div>
        </div>

        {/* Center: Metadata */}
        <div className="min-w-0 flex-1 flex flex-col justify-center">
          <div className="flex items-center gap-2">
            <h4
              className={`text-xs sm:text-sm font-bold truncate group-hover:text-secondary transition-colors ${
                dark ? "text-white" : "text-gray-900"
              }`}
            >
              {title || item?.title || "Product Title"}
            </h4>
          </div>

          <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-zinc-400 truncate mt-0.5">
            <span className="truncate">
              {author || item?.author || cast || item?.cast || "BAB Creator"}
            </span>
            <span className="text-zinc-600">•</span>
            <span className="text-[10px] font-semibold text-secondary uppercase truncate">
              {returnSubCategoryName(category || item?.category) || "General"}
            </span>
          </div>

          {/* Badges / Rating row */}
          <div className="flex items-center gap-2 mt-1">
            <span
              className={`text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded-full ${access.badgeClass}`}
            >
              {access.isExclusive && "★ "}
              {access.badgeText}
            </span>
            {rating && (
              <span className="text-[10px] text-zinc-400 font-semibold flex items-center gap-0.5">
                <span className="text-yellow-400 text-xs">★</span>
                <span>{Number(rating).toFixed(1)}</span>
              </span>
            )}
            {views ? (
              <span className="text-[10px] text-zinc-500 hidden sm:inline">
                {views} views
              </span>
            ) : null}
          </div>
        </div>

        {/* Right: Price & Action */}
        <div className="flex flex-col items-end justify-center gap-1.5 flex-shrink-0 pl-1">
          <span
            className={`text-xs font-black tracking-tight ${
              access.isFree
                ? "text-emerald-400"
                : access.isExclusive
                ? "text-amber-400"
                : "text-white"
            }`}
          >
            {access.formattedPrice}
          </span>
          <div className="w-8 h-8 rounded-full bg-secondary/10 group-hover:bg-secondary text-secondary group-hover:text-white transition-all flex items-center justify-center border border-secondary/25 shadow-sm active:scale-90">
            <ChevronRightIcon className="h-4 w-4 stroke-[2.5]" />
          </div>
        </div>
      </div>
    );
  }

  // MODERN GRID CARD (Poster format)
  return (
    <Card
      onClick={onClickCard}
      className={`group w-full max-w-full sm:max-w-[13rem] xl:max-w-[14rem] shadow-md my-1.5 sm:my-2 hover:cursor-pointer hover:-translate-y-1.5 hover:shadow-xl active:scale-[0.98] transition-all duration-300 ease-out overflow-hidden rounded-2xl border ${
        dark
          ? "bg-zinc-900 border-zinc-800/80 hover:bg-zinc-850 hover:border-zinc-700 text-white shadow-zinc-950/40"
          : "bg-white border-gray-150 hover:bg-orange-50/30 hover:border-orange-100 text-black shadow-gray-200/50"
      }`}
    >
      <CardHeader
        className="relative mx-0 mt-0 overflow-hidden rounded-t-2xl rounded-b-none h-[9.5rem] sm:h-[12.5rem] xl:h-[13.5rem]"
        floated={false}
        color="transparent"
      >
        <img
          className="h-full w-full object-cover transform group-hover:scale-105 transition-transform duration-500"
          src={image || item?.image || dummuImageUrl}
          alt={title || item?.title || "Cover Image"}
          onError={(e) => {
            if (e.currentTarget.src !== dummuImageUrl) {
              e.currentTarget.src = dummuImageUrl;
            }
          }}
        />
        {/* Gradient backdrop overlay for text contrast */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-black/40 pointer-events-none" />

        {/* Action Icon & Label Centered on Image (reveals on hover) */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center pointer-events-none p-2 text-center">
          <div className="bg-secondary text-white p-2.5 rounded-full shadow-lg transform scale-75 group-hover:scale-100 transition-transform duration-300 ease-out mb-1.5">
            <EyeIcon className="h-5 w-5 stroke-[2.5]" />
          </div>
          <span className="text-[10px] sm:text-[11px] font-bold text-white tracking-wide bg-black/80 px-2 py-0.5 rounded-md backdrop-blur-sm border border-white/10 shadow">
            {access.isFree
              ? "Read / Watch Free"
              : access.isExclusive
              ? "Subscriber VIP"
              : access.isPpv
              ? `Rent PPV (${access.formattedPrice})`
              : `Buy (${access.formattedPrice})`}
          </span>
        </div>

        {/* Media Type pill overlay (Top Left) */}
        <div className="absolute top-2 left-2 z-10 flex items-center gap-1 px-1.5 py-0.5 bg-black/70 backdrop-blur-md border border-white/10 rounded-full text-white text-[9px] sm:text-[10px] font-bold shadow-md">
          {(type === "Video" || currentItem?.type === "Video") && (
            <FilmIcon className="h-3 w-3 text-secondary" />
          )}
          {(type === "Audio" || currentItem?.type === "Audio") && (
            <MusicalNoteIcon className="h-3.5 w-3.5 text-secondary" />
          )}
          {(type === "Book" || currentItem?.type === "Book") && (
            <BookOpenIcon className="h-3.5 w-3.5 text-secondary" />
          )}
          <span className="capitalize">{type || currentItem?.type || "Item"}</span>
        </div>

        {/* Access Type Badge overlay (Top Right - Compact to prevent collision) */}
        <div
          className={`absolute top-2 right-2 z-10 flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wide backdrop-blur-md shadow-md ${access.badgeClass}`}
        >
          {access.isExclusive && <span>★</span>}
          {access.isPpv && <span>🎟</span>}
          {access.isFree && <span>🎁</span>}
          <span>{access.badgeText}</span>
        </div>

        {/* Star Rating pill overlay (Bottom Right if available) */}
        {rating && (
          <div className="absolute bottom-2 right-2 z-10 flex items-center gap-0.5 px-1.5 py-0.5 bg-black/75 backdrop-blur-md border border-white/10 rounded-md text-white text-[9px] sm:text-[10px] font-bold shadow-md">
            <span className="text-yellow-400 text-xs">★</span>
            <span>{Number(rating).toFixed(1)}</span>
          </div>
        )}
      </CardHeader>
      <CardBody className="px-2.5 sm:px-3 pt-2 pb-2.5 flex flex-col justify-between flex-1">
        <div>
          <div className="mb-0.5 flex items-center justify-between">
            <Typography
              variant="h6"
              color={dark ? "white" : "blue-gray"}
              className="font-bold text-xs sm:text-sm lg:text-base !line-clamp-1 group-hover:text-secondary transition-colors"
            >
              {title || item?.title || "Product Title"}
            </Typography>
          </div>
          <Typography
            variant="paragraph"
            className={`!line-clamp-1 text-[11px] sm:text-xs font-normal ${
              dark ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {author || item?.author || cast || item?.cast || "BAB Creator"}
          </Typography>
        </div>

        <div>
          {/* Subcategory & Views Row */}
          <div className="mt-2 flex items-center justify-between text-xs">
            <span
              className={`text-[9px] sm:text-[10px] font-semibold px-1.5 py-0.5 rounded-md uppercase tracking-wider truncate max-w-[65%] ${
                dark
                  ? "bg-secondary/10 text-secondary"
                  : "bg-orange-50 text-secondary border border-orange-100/50"
              }`}
            >
              {returnSubCategoryName(category || item?.category) || "General"}
            </span>
            {views ? (
              <span
                className={`text-[9px] sm:text-[10px] font-medium ${
                  dark ? "text-gray-500" : "text-gray-400"
                }`}
              >
                {views} views
              </span>
            ) : null}
          </div>

          {/* Access Model & Pricing Row */}
          <div className="mt-2 pt-2 flex items-center justify-between border-t border-zinc-800/60">
            <span className="text-[10px] sm:text-[11px] font-medium text-zinc-400">
              {access.isFree
                ? "Access"
                : access.isExclusive
                ? "Plan"
                : access.isPpv
                ? "Rental"
                : "Price"}
            </span>
            <div className="text-right">
              {access.isFree ? (
                <span className="text-[10px] sm:text-xs font-black text-emerald-400 tracking-wide bg-emerald-500/10 px-1.5 sm:px-2 py-0.5 rounded border border-emerald-500/20">
                  FREE
                </span>
              ) : access.isExclusive ? (
                <span className="text-[10px] sm:text-[11px] font-extrabold text-amber-400 tracking-wide bg-amber-500/10 px-1.5 sm:px-2 py-0.5 rounded border border-amber-500/20 inline-flex items-center gap-1">
                  <span>★</span> VIP Only
                </span>
              ) : access.isPpv ? (
                <div className="inline-flex items-center gap-1">
                  <span className="text-[9px] font-extrabold text-purple-400 uppercase bg-purple-500/10 px-1 py-0.5 rounded">
                    PPV
                  </span>
                  <span className="text-[11px] sm:text-xs font-extrabold text-white">
                    {access.formattedPrice}
                  </span>
                </div>
              ) : (
                <span className="text-[11px] sm:text-xs font-black text-white">
                  {access.formattedPrice}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
